// src/components/Header.tsx
// Thin app bar: current page title (left), global search / ⌘K palette (center),
// theme picker + settings quick-access (right). Brand + account live in the Sidebar.
import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  Box,
  Tooltip,
  Divider,
  Theme,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeContext, getThemeIcon, THEMES } from '../context/ThemeProvider';
import {
  SECTIONS,
  SETTINGS_ITEMS,
  SIDEBAR_WIDTH_EXPANDED,
  SIDEBAR_WIDTH_COLLAPSED,
} from './Sidebar';
import CommandPalette from './CommandPalette';

interface HeaderProps {
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

interface TitleEntry {
  path: string;
  title: string;
}

// Every nav destination, flattened, for pathname → page-title resolution.
const NAV_TITLES: TitleEntry[] = [
  ...SECTIONS.flatMap((section) =>
    section.items.map((item) => ({ path: item.path, title: item.label }))
  ),
  ...SETTINGS_ITEMS.map((item) => ({ path: item.path, title: item.label })),
];

// Dynamic routes that deserve a more specific title than their prefix match.
const TITLE_OVERRIDES: { pattern: RegExp; title: string }[] = [
  { pattern: /^\/orders\/[^/]+\/edit(\/|$)/, title: 'Edit Order' },
  { pattern: /^\/products\/edit\//, title: 'Edit Product' },
  { pattern: /^\/products\/add(\/|$)/, title: 'Add Product' },
  { pattern: /^\/fulfillments\//, title: 'Fulfillment' },
  { pattern: /^\/settings\/?$/, title: 'Settings' },
];

const resolvePageTitle = (pathname: string): string => {
  const override = TITLE_OVERRIDES.find((entry) => entry.pattern.test(pathname));
  if (override) return override.title;
  const match = NAV_TITLES.filter(
    (entry) => pathname === entry.path || pathname.startsWith(`${entry.path}/`)
  ).sort((a, b) => b.path.length - a.path.length)[0];
  return match?.title ?? 'Dashboard';
};

const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

const Header: React.FC<HeaderProps> = ({ isSidebarOpen, setSidebarOpen }) => {
  const { themeMode, setThemeMode } = useThemeContext();
  const [themeAnchorEl, setThemeAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isTabletOrMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const pageTitle = useMemo(() => resolvePageTitle(pathname), [pathname]);

  // On desktop the permanent sidebar owns the left edge; offset the bar past it.
  const sidebarWidth = isTabletOrMobile
    ? 0
    : isSidebarOpen
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED;

  // Global ⌘K / Ctrl+K opens the command palette (Esc inside the palette closes it).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeAnchorEl(null);
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsAnchorEl(null);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: `calc(100% - ${sidebarWidth}px)`,
        ml: `${sidebarWidth}px`,
        transition: 'width 0.3s ease, margin-left 0.3s ease',
        backdropFilter: 'blur(20px)',
        background: (theme) => alpha(theme.palette.background.paper, 0.9),
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          minHeight: '52px !important',
        }}
      >
        {/* Left: mobile menu toggle + current page title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
          {isTabletOrMobile && (
            <IconButton
              edge="start"
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              sx={{
                color: 'text.primary',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            noWrap
            component="h1"
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: 'text.primary',
              maxWidth: { xs: 140, sm: 220, md: 320 },
            }}
          >
            {pageTitle}
          </Typography>
        </Box>

        {/* Center: search bar (opens the command palette) */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <ButtonBase
            onClick={() => setIsPaletteOpen(true)}
            aria-label="Open search"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1,
              width: '100%',
              maxWidth: 440,
              height: 34,
              px: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
              color: 'text.secondary',
              textAlign: 'left',
              transition: 'border-color 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.24),
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography noWrap sx={{ flex: 1, fontSize: '0.8125rem', color: 'text.secondary' }}>
              Search orders, products, pages…
            </Typography>
            <Box
              component="kbd"
              sx={{
                flexShrink: 0,
                px: 0.75,
                py: 0.1,
                borderRadius: 0.75,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                fontFamily: 'inherit',
                fontSize: '0.6875rem',
                lineHeight: 1.6,
                color: 'text.secondary',
              }}
            >
              {IS_MAC ? '⌘K' : 'Ctrl K'}
            </Box>
          </ButtonBase>
          <IconButton
            onClick={() => setIsPaletteOpen(true)}
            aria-label="Open search"
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: 'text.secondary' }}
          >
            <SearchIcon />
          </IconButton>
        </Box>

        {/* Right: theme picker + settings quick-access */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Tooltip
            title={`Theme: ${THEMES.find((t) => t.id === themeMode)?.label ?? themeMode}`}
            arrow
          >
            <IconButton
              onClick={handleThemeMenuOpen}
              size="medium"
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {getThemeIcon(themeMode)}
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={themeAnchorEl}
            open={Boolean(themeAnchorEl)}
            onClose={handleThemeMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  borderLeft: '1px solid',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Theme
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pick a look for the dashboard
              </Typography>
            </Box>
            <Divider />
            {THEMES.map((themeOption) => {
              const isActive = themeOption.id === themeMode;
              return (
                <MenuItem
                  key={themeOption.id}
                  selected={isActive}
                  onClick={() => {
                    setThemeMode(themeOption.id);
                    handleThemeMenuClose();
                  }}
                  sx={{
                    py: 1.25,
                    px: 2,
                    gap: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {themeOption.swatch.map((color) => (
                      <Box
                        key={color}
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: color,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    ))}
                  </Box>
                  <ListItemText
                    primary={themeOption.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                  {isActive && (
                    <CheckIcon fontSize="small" sx={{ color: 'primary.main', ml: 'auto' }} />
                  )}
                </MenuItem>
              );
            })}
          </Menu>

          <Tooltip title="Settings" arrow>
            <IconButton
              onClick={handleSettingsMenuOpen}
              size="medium"
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'action.hover',
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* Settings quick-access — mirrors the sidebar's canonical list */}
          <Menu
            anchorEl={settingsAnchorEl}
            open={Boolean(settingsAnchorEl)}
            onClose={handleSettingsMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 230,
                maxHeight: 460,
                borderRadius: 2,
                overflow: 'auto',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Settings
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Quick access to configuration
              </Typography>
            </Box>
            <Divider />
            {SETTINGS_ITEMS.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  handleSettingsMenuClose();
                }}
                sx={{
                  py: 1.25,
                  px: 2,
                  gap: 1.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                    pl: 3,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <item.icon fontSize="small" sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Toolbar>

      <CommandPalette open={isPaletteOpen} onClose={() => setIsPaletteOpen(false)} />
    </AppBar>
  );
};

export default Header;
